import React, { useState, useEffect } from "react";
import { router } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { X, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";

interface Tag {
  id: number;
  name: string;
  color: string;
}

interface Template {
  id: number;
  name: string;
  tag_ids?: number[];
  tags?: Tag[];
}

interface TemplateTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;
  availableTags: Tag[];
  offerId: number;
}

export default function TemplateTagDialog({
  open,
  onOpenChange,
  template,
  availableTags = [],
  offerId,
}: TemplateTagDialogProps) {
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && template) {
      setSelectedTagId(template.tag_ids && template.tag_ids.length > 0 ? template.tag_ids[0] : null);
    }
  }, [template, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;
    
    setLoading(true);

    router.patch(
      `/offers/${offerId}/templates/${template.id}/tags`,
      {
        tag_ids: selectedTagId ? [selectedTagId] : [],
      },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          toast.success("Tag updated successfully");
          onOpenChange(false);
        },
        onError: () => {
          toast.error("Failed to update tag");
        },
        onFinish: () => {
          setLoading(false);
        },
      }
    );
  };

  const getColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-800 border-green-200";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "red":
        return "bg-red-100 text-red-800 border-red-200";
      case "blue":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "purple":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "gray":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TagIcon className="w-4 h-4" />
              Manage Template Tag
            </DialogTitle>
            <DialogDescription>
              Assign a tag to "{template.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {availableTags.length > 0 ? (
              <div className="space-y-2">
                <Label>Select Tag</Label>
                <RadioGroup
                  value={selectedTagId?.toString() || ""}
                  onValueChange={(value) => setSelectedTagId(value ? parseInt(value) : null)}
                  disabled={loading}
                >
                  <div className="border rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                    <div className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded">
                      <RadioGroupItem value="" id="tag-none" />
                      <label
                        htmlFor="tag-none"
                        className="flex-1 cursor-pointer text-sm text-gray-600"
                      >
                        No tag
                      </label>
                    </div>
                    {availableTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded"
                      >
                        <RadioGroupItem
                          value={tag.id.toString()}
                          id={`tag-${tag.id}`}
                        />
                        <label
                          htmlFor={`tag-${tag.id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorClass(
                              tag.color
                            )}`}
                          >
                            {tag.name}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
                {selectedTagId && (
                  <div className="flex items-center gap-2 mt-2 p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-600">Selected:</span>
                    {(() => {
                      const tag = availableTags.find((t) => t.id === selectedTagId);
                      if (!tag) return null;
                      return (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getColorClass(
                            tag.color
                          )}`}
                        >
                          {tag.name}
                          <button
                            type="button"
                            onClick={() => setSelectedTagId(null)}
                            className="ml-1 hover:opacity-75"
                            disabled={loading}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <TagIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No tags available</p>
                <p className="text-xs mt-1">Admin needs to create tags first</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || availableTags.length === 0}
              className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}